/**
 * useAudioPlayback — React hook for sequential TTS audio playback.
 *
 * EPIC-01-06 AC-5: Plays back AI voice audio received as base64 PCM-16 chunks
 * from the pipeline WebSocket, using Web Audio API.
 *
 * ## Audio format
 * Incoming chunks: 16-bit signed PCM, little-endian, 16 kHz mono
 * (matches Polly OutputFormat="pcm", SampleRate="16000").
 *
 * ## Scheduling strategy
 * Each chunk is decoded and scheduled at `Math.max(ctx.currentTime, nextPlayTime)`
 * to ensure gap-free sequential playback without overlapping.
 * `nextPlayTime` advances by `buffer.duration` after each chunk.
 *
 * ## Browser autoplay policy
 * Chrome/Edge/Safari suspend a newly created AudioContext until the page has
 * received a user gesture (click, keydown, etc.).  `primeAudioContext()` MUST
 * be called from within a user-gesture handler (e.g. the mic button onClick)
 * before the first audio chunk arrives over the WebSocket.  Once the context
 * is resumed from a gesture, subsequent `enqueueChunk` calls from WebSocket
 * callbacks work without restriction.
 *
 * ## PII
 * No audio data is logged (CLAUDE.md §PII).
 *
 * @module useAudioPlayback
 */

import { useCallback, useRef } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Output sample rate for Amazon Polly (PCM-16 mono). */
const PCM_SAMPLE_RATE = 16_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseAudioPlaybackReturn {
  /**
   * Call this from a user-gesture handler (e.g. mic button onClick) to create
   * and resume the AudioContext BEFORE the first audio chunk arrives.
   * This is the only reliable way to satisfy the browser autoplay policy.
   */
  primeAudioContext: () => Promise<void>;
  /**
   * Decode and schedule a base64 PCM-16 chunk for sequential playback.
   * Returns a Promise — await it or fire-and-forget with `void`.
   */
  enqueueChunk: (base64Pcm: string) => Promise<void>;
  /** Close the AudioContext and reset the playback cursor. */
  stopPlayback: () => void;
  /**
   * Stop any AI audio currently playing/scheduled — used for barge-in so the
   * agent can cut the AI off mid-reply. Keeps the AudioContext alive (unlike
   * stopPlayback) so the next AI turn plays without re-priming.
   */
  cancelPlayback: () => void;
  /**
   * True while AI audio is currently coming out of the speakers (there is still
   * scheduled playback ahead of `currentTime`), plus an optional tail to cover
   * acoustic decay. Used to gate the microphone (half-duplex) so the AI's own
   * voice is not captured and re-transcribed as the agent — which otherwise
   * creates the speak→mic→STT feedback loop.
   *
   * @param tailSeconds - extra window after the last scheduled sample (default 0.25s).
   */
  isOutputActive: (tailSeconds?: number) => boolean;
}

/**
 * Plays base64 PCM-16 audio chunks received from the pipeline WebSocket
 * through the Web Audio API in order, without gaps.
 *
 * @param sampleRate - Sample rate in Hz (default: 16 000 to match Polly).
 *
 * @example
 * ```tsx
 * const { primeAudioContext, enqueueChunk, stopPlayback } = useAudioPlayback();
 *
 * // In mic button onClick (user gesture — REQUIRED before audio will play):
 * await primeAudioContext();
 *
 * // On WS message type === 'audio_chunk':
 * void enqueueChunk(msg.data.data);
 *
 * // On session end:
 * stopPlayback();
 * ```
 */
export function useAudioPlayback(sampleRate: number = PCM_SAMPLE_RATE): UseAudioPlaybackReturn {
  const ctxRef = useRef<AudioContext | null>(null);
  const nextTimeRef = useRef<number>(0);
  // Scheduled-but-not-finished AI audio sources, so a barge-in can stop them.
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  // ---------------------------------------------------------------------------
  // Internal: lazily create AudioContext; re-create if closed
  // ---------------------------------------------------------------------------

  const getCtx = useCallback((): AudioContext => {
    if (ctxRef.current === null || ctxRef.current.state === 'closed') {
      ctxRef.current = new AudioContext({ sampleRate });
      nextTimeRef.current = 0;
    }
    return ctxRef.current;
  }, [sampleRate]);

  // ---------------------------------------------------------------------------
  // primeAudioContext — MUST be called from a user gesture
  // ---------------------------------------------------------------------------

  /**
   * Creates the AudioContext (if not yet created) and resumes it.
   *
   * This MUST be called from a synchronous user-gesture handler so the browser
   * considers the resume "user-initiated".  After this resolves, the context
   * stays in `running` state for the lifetime of the session and all subsequent
   * `enqueueChunk` calls from WebSocket callbacks will play without restriction.
   */
  const primeAudioContext = useCallback(async (): Promise<void> => {
    const ctx = getCtx();
    if (ctx.state !== 'running') {
      await ctx.resume();
    }
  }, [getCtx]);

  // ---------------------------------------------------------------------------
  // enqueueChunk — decode + schedule a PCM-16 chunk
  // ---------------------------------------------------------------------------

  const enqueueChunk = useCallback(
    async (base64Pcm: string): Promise<void> => {
      if (!base64Pcm) return;

      const ctx = getCtx();

      // Ensure the context is running.
      // If primeAudioContext() was called from a user gesture this is a no-op.
      // If not (e.g. in tests or edge cases), this attempts a best-effort resume.
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      // Decode base64 → binary bytes
      let binary: string;
      try {
        binary = atob(base64Pcm);
      } catch {
        // Malformed base64 — skip silently (non-fatal)
        return;
      }
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      // Interpret as little-endian Int16 PCM → Float32 [-1, +1]
      const int16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = (int16[i] ?? 0) / 0x8000;
      }

      if (float32.length === 0) return;

      // Build AudioBuffer and schedule gaplessly after the previous chunk
      const buffer = ctx.createBuffer(/* channels */ 1, float32.length, sampleRate);
      buffer.getChannelData(0).set(float32);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      sourcesRef.current.add(source);
      source.onended = () => sourcesRef.current.delete(source);

      const startAt = Math.max(ctx.currentTime, nextTimeRef.current);
      source.start(startAt);
      nextTimeRef.current = startAt + buffer.duration;
    },
    [getCtx, sampleRate],
  );

  // ---------------------------------------------------------------------------
  // cancelPlayback — stop AI audio immediately (barge-in) without tearing down
  // the AudioContext, so the agent can cut the AI off mid-reply.
  // ---------------------------------------------------------------------------

  const cancelPlayback = useCallback((): void => {
    for (const s of sourcesRef.current) {
      try { s.stop(); } catch { /* already stopped */ }
    }
    sourcesRef.current.clear();
    const ctx = ctxRef.current;
    nextTimeRef.current = ctx ? ctx.currentTime : 0;
  }, []);

  // ---------------------------------------------------------------------------
  // stopPlayback
  // ---------------------------------------------------------------------------

  const stopPlayback = useCallback((): void => {
    for (const s of sourcesRef.current) {
      try { s.stop(); } catch { /* already stopped */ }
    }
    sourcesRef.current.clear();
    void ctxRef.current?.close();
    ctxRef.current = null;
    nextTimeRef.current = 0;
  }, []);

  // ---------------------------------------------------------------------------
  // isOutputActive — is AI audio still playing (used to gate the mic)
  // ---------------------------------------------------------------------------

  const isOutputActive = useCallback((tailSeconds = 0.25): boolean => {
    const ctx = ctxRef.current;
    if (ctx === null || ctx.state === 'closed') return false;
    return ctx.currentTime < nextTimeRef.current + tailSeconds;
  }, []);

  return { primeAudioContext, enqueueChunk, stopPlayback, cancelPlayback, isOutputActive };
}
